import React, { useRef, useState } from 'react';
import {
  Button,
  Col,
  Container,
  Form,
  Image,
  InputGroup,
  Modal,
  Row,
  Tab,
  Tabs,
} from 'react-bootstrap';
import {
  AlertTriangle,
  Calendar,
  Eye,
  Info,
  Instagram,
  Mail,
  MapPin,
  User,
} from 'react-feather';
import axios from 'axios';
import toast from 'react-hot-toast';
import { formatNumberWithout } from '../helper';
import SeatMapCircle from './Seat/SeatMapCircle';
import Seat from './Seat/SeatMap';

const EVENT_ID = 137;

const EVENT = {
  venue: 'The Kasablanka Hall, Jakarta',
  date: '26 Desember 2026',
  csNumber: '6282330483969',
  csLabel: '+62 823-3048-3969',
  instagram: 'soul.concert',
};

// TODO(soul8): arahkan ke aset final Soul 8 (mis. '../images/soul8/logo.webp').
// Latar batik diatur lewat `.home` di style.css.
const ART = {
  logo: require('../images/soul/logo.avif'),
  hero: require('../images/soul/hero-lineup.webp'),
  seatingLayout: require('../images/soul/layout.png'),
};

const PAYMENT_METHODS = [
  {
    code: 'BRI',
    label: 'BRI Virtual Account',
    logo: require('../images/mobile/bank/bri.png'),
  },
  {
    code: 'BNI',
    label: 'BNI Virtual Account',
    logo: require('../images/mobile/bank/bni.png'),
  },
  {
    code: 'BSI',
    label: 'BSI Virtual Account',
    logo: require('../images/mobile/bank/bsi.png'),
  },
  {
    code: 'MANDIRI',
    label: 'Mandiri Virtual Account',
    logo: require('../images/mobile/bank/mandiri.png'),
  },
  {
    code: 'QRIS',
    label: 'QRIS',
    logo: require('../images/mobile/bank/QRIS-1.png'),
  },
];

const SectionTitle = ({ children }) => (
  <div className="plaque">
    <h2>{children}</h2>
  </div>
);

// 'left' -> A, 'right'/'right_circle' -> B. Sebelumnya kode ini ditempel di
// belakang tiap nomor kursi ("A23 (A), A22 (A), …"); sekarang cukup sekali
// sebagai label blok.
const blockLabel = (loc2stage) => {
  if (loc2stage === 'left') return 'A';
  if (loc2stage === 'right' || loc2stage === 'right_circle') return 'B';
  return loc2stage || '';
};

// "A20, A21, A22, A23" -> "Baris A · Kursi 20–23". Nomor yang tidak berurutan
// tetap dirinci, dan nama yang tidak berpola huruf+angka dibiarkan apa adanya.
const summariseSeats = (option) => {
  const byRow = new Map();

  option.forEach((seat) => {
    const parsed = String(seat.name).match(/^([A-Za-z]+)\s*(\d+)$/);
    const row = parsed ? parsed[1] : String(seat.name);
    if (!byRow.has(row)) byRow.set(row, []);
    byRow.get(row).push(parsed ? Number(parsed[2]) : null);
  });

  return [...byRow.entries()].map(([row, numbers]) => {
    const seats = numbers.filter((n) => n !== null).sort((a, b) => a - b);
    if (!seats.length) return row;

    const contiguous = seats.every((n, i) => i === 0 || n === seats[i - 1] + 1);
    const list =
      contiguous && seats.length > 1
        ? `${seats[0]}–${seats[seats.length - 1]}`
        : seats.join(', ');

    return `Baris ${row} · Kursi ${list}`;
  });
};

// Membaca daftar rekomendasi dari respons, toleran terhadap beberapa bentuk
// yang mungkin dikirim server. Mengembalikan null kalau isinya tidak terbaca —
// dibedakan dari [] yang berarti server memang tidak punya rekomendasi. Tanpa
// pembedaan ini, salah baca bentuk data tampil sama seperti "kursi habis".
const extractOptions = (payload) => {
  const isGroupList = (value) =>
    Array.isArray(value) && value.every((group) => Array.isArray(group));

  for (const candidate of [
    payload?.data?.options,
    payload?.data,
    payload?.options,
  ]) {
    if (isGroupList(candidate)) return candidate;

    // Sebagian backend mengirim daftar berindeks sebagai objek.
    if (
      candidate &&
      typeof candidate === 'object' &&
      !Array.isArray(candidate)
    ) {
      const values = Object.values(candidate);
      if (values.length && isGroupList(values)) return values;
    }
  }
  return null;
};

const WhatsAppGlyph = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.85 9.85 0 0 0 12.04 2Zm0 1.67c2.2 0 4.27.86 5.83 2.42a8.2 8.2 0 0 1 2.41 5.82c0 4.55-3.7 8.24-8.25 8.24a8.24 8.24 0 0 1-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.26-8.24Zm-2.6 4.02c-.16 0-.43.06-.65.3-.23.24-.87.85-.87 2.06s.89 2.39 1.01 2.56c.13.16 1.72 2.72 4.23 3.71 2.09.83 2.52.66 2.97.62.45-.04 1.46-.6 1.67-1.18.2-.58.2-1.07.14-1.18-.06-.1-.23-.16-.47-.28-.25-.12-1.46-.72-1.68-.8-.23-.09-.39-.13-.55.12-.17.24-.64.8-.78.97-.15.16-.29.18-.53.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.7-.15-.25-.02-.38.1-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.41.09-.17.04-.31-.02-.43-.06-.12-.55-1.33-.75-1.82-.2-.48-.4-.41-.55-.42h-.47Z" />
  </svg>
);

const Home = () => {
  const [emailVal, setEmailVal] = useState('');
  const [coupon, setCoupon] = useState('');
  const [couponData, setCouponData] = useState({});
  const [modalSeatMap, setModalSeatMap] = useState(false);
  const [modalInvoice, setModalInvoice] = useState(false);
  const [disableRes, setDisableRes] = useState(false);
  const [seatMap, setSeatMap] = useState([]);
  const [seatMapSelectId, setSeatMapSelectId] = useState([]);
  const [customerData, setCustomerData] = useState({});
  const [listSeatSelected, setListSeatSelected] = useState('');
  const [priceSeatSelected, setPriceSeatSelected] = useState('');
  const [selectedTicketId, setSelectedTicketId] = useState('');
  const [selectedTicketIdRes, setSelectedTicketIdRes] = useState('');
  const [bookingData, setBookingData] = useState([]);
  const [optionSeat, setOptionSeat] = useState([]);
  const [seatBooked, setSeatBooked] = useState([]);
  const [selectedSeatIds, setSelectedIds] = useState([]);
  const [bookingStatus, setBookingStatus] = useState(false);
  const [key, setKey] = useState('home');
  const [emailStatus, setEmailStatus] = useState(false);
  const [indexSelect, setIndexSelect] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  // Jumlah kursi yang dibayar pada tab kategori yang sedang aktif.
  const [requiredSeatCount, setRequiredSeatCount] = useState(0);
  const [sortBy, setSortBy] = useState('default');
  const [blockFilter, setBlockFilter] = useState('all');
  const [onlyUsable, setOnlyUsable] = useState(false);
  // 'idle' | 'loading' | 'ready' | 'error' — supaya gagal-memuat tidak lagi
  // tampil sebagai "belum ada rekomendasi".
  const [recoStatus, setRecoStatus] = useState({ state: 'idle', message: '' });
  const section3Ref = useRef(null);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Booking code bisa mengandung tanda hubung, jadi hanya pisahkan pada tanda
  // hubung pertama.
  const splitTabKey = (tabKey) => {
    const separator = String(tabKey).indexOf('-');
    if (separator === -1) {
      return { ticketId: String(tabKey).trim(), bookingId: '' };
    }
    return {
      ticketId: String(tabKey).slice(0, separator).trim(),
      bookingId: String(tabKey)
        .slice(separator + 1)
        .trim(),
    };
  };

  const handleBooking = async () => {
    if (emailVal === '') {
      return toast.error('Email tidak boleh kosong');
    }

    const params = {
      email: emailVal,
      event_id: EVENT_ID,
    };

    try {
      const response = await axios(
        `${process.env.REACT_APP_API_URL}/seat-payment/get-booking`,
        {
          headers: {
            'Content-Type': 'application/json',
            token: process.env.REACT_APP_API_KEY,
          },
          method: 'POST',
          data: JSON.stringify(params),
        },
      );

      if (response.data.status) {
        setEmailStatus(true);
        setCustomerData(response.data.data.data);
        setBookingData(response.data.data.booking);
        const combine = combineTickets(response.data.data.booked_seats);
        setSeatBooked(combine);
        setBookingStatus(true);

        if (response.data.data.booking.length > 0) {
          const firstBooking = response.data.data.booking[0];
          const firstBookingKey = `${firstBooking.ticket_id}-${firstBooking.booking_code}`;
          setKey(firstBookingKey);
          getRecomendation(firstBookingKey, firstBooking.allocated_seat);
        }
      } else {
        toast.error('Pemesanan tidak ditemukan, periksa kembali email Anda');
      }
    } catch (error) {
      toast.error('Gagal menghubungi server. Silakan coba lagi.');
    }
  };

  const handleRemoveState = () => {
    setSeatMap([]);
    setSeatMapSelectId([]);
    setCustomerData({});
    setListSeatSelected('');
    setPriceSeatSelected('');
    setSelectedTicketId('');
    setBookingData([]);
    setOptionSeat([]);
    setSeatBooked([]);
    setBookingStatus(false);
    setKey('home');
    setEmailStatus(false);
    setEmailVal('');
    setIndexSelect('');
    setPaymentMethod('');
    // Kupon dan kursi terpilih ikut direset supaya tidak terbawa ke pemesanan
    // milik email lain.
    setCoupon('');
    setCouponData({});
    setSelectedIds([]);
    setSelectedTicketIdRes('');
    setRecoStatus({ state: 'idle', message: '' });
  };

  const handleValidateCoupon = async () => {
    if (coupon === '') {
      return toast.error('Kode kupon tidak boleh kosong');
    }

    const params = {
      code: coupon,
      email: emailVal,
    };

    try {
      const response = await axios(
        `${process.env.REACT_APP_API_URL}/seat-payment/check-coupon`,
        {
          headers: {
            'Content-Type': 'application/json',
            token: process.env.REACT_APP_API_KEY,
          },
          method: 'POST',
          data: JSON.stringify(params),
        },
      );

      if (response.data.status) {
        setCouponData(response?.data?.data);
        toast.success('Kupon berhasil digunakan');
      } else {
        setCouponData({});
        toast.error('Kupon tidak valid');
      }
    } catch (error) {
      toast.error('Gagal memeriksa kupon. Silakan coba lagi.');
    }
  };

  const getRecomendation = async (tabKey, allocatedSeat) => {
    try {
      setOptionSeat([]);
      setRecoStatus({ state: 'loading', message: '' });
      setRequiredSeatCount(Number(allocatedSeat) || 0);
      // Blok yang tersedia berbeda antar kategori, jadi filter direset agar
      // pindah tab tidak menghasilkan daftar kosong tanpa sebab yang jelas.
      setBlockFilter('all');
      const { ticketId: ticket_id, bookingId: booking_id } =
        splitTabKey(tabKey);
      setSelectedTicketIdRes(ticket_id);
      const params = {
        ticket_id: ticket_id,
        booking_id: booking_id,
        event_id: EVENT_ID,
      };
      const response = await axios(
        `${process.env.REACT_APP_API_URL}/seat-payment/get-recomendation`,
        {
          headers: {
            'Content-Type': 'application/json',
            token: process.env.REACT_APP_API_KEY,
          },
          method: 'POST',
          data: JSON.stringify(params),
        },
      );

      // Server membalas HTTP 200 dengan status:false untuk penolakan. Dulu
      // cabang ini tidak ada else-nya, sehingga penolakan tampak sama persis
      // dengan "tidak ada kursi" — pesan dari server pun hilang.
      if (!response.data.status) {
        setRecoStatus({
          state: 'error',
          message:
            response.data.message || 'Server menolak permintaan rekomendasi.',
        });
        return;
      }

      const recommendations = extractOptions(response.data);
      if (recommendations === null) {
        setRecoStatus({
          state: 'error',
          message: 'Format rekomendasi dari server tidak dikenali.',
        });
        return;
      }

      // Rekomendasi ditampilkan apa adanya — tidak dipangkas maupun disaring
      // diam-diam. Yang jumlahnya tidak sama dengan jatah booking tetap
      // muncul tapi tidak bisa dipilih, supaya pelanggan tahu opsi itu ada
      // sekaligus tahu kenapa tidak bisa diambil.
      setOptionSeat(recommendations.filter((option) => option.length > 0));
      setRecoStatus({ state: 'ready', message: '' });
    } catch (error) {
      const httpStatus = error?.response?.status;
      setRecoStatus({
        state: 'error',
        message: httpStatus
          ? `Server gagal memproses permintaan (HTTP ${httpStatus}).`
          : 'Tidak bisa menghubungi server.',
      });
    }
  };

  // Dipakai tombol "Coba lagi" pada keadaan gagal.
  const reloadRecommendations = () => {
    const booking = bookingData.find(
      (item) => `${item.ticket_id}-${item.booking_code}` === key,
    );
    getRecomendation(key, booking?.allocated_seat);
  };

  const handleReservSeat = async () => {
    // Penjaga terakhir. Tombolnya sudah dinonaktifkan saat jumlahnya tidak
    // cocok, tapi jumlah kursi menentukan yang dibayar pelanggan — jadi
    // divalidasi ulang tepat sebelum dikirim, bukan hanya di lapisan tampilan.
    if (requiredSeatCount > 0 && seatMapSelectId.length !== requiredSeatCount) {
      return toast.error(
        `Jumlah kursi tidak sesuai: booking Anda ${requiredSeatCount} kursi, yang terpilih ${seatMapSelectId.length} kursi.`,
      );
    }

    if (seatMapSelectId.length > 0) {
      try {
        if (priceSeatSelected !== 0) {
          if (paymentMethod === '') {
            setDisableRes(false);
            return toast.error('Pilih metode pembayaran');
          }
        }

        setDisableRes(true);
        const { bookingId: booking_id } = splitTabKey(key);

        const params = {
          seat_selected: seatMapSelectId.join(','),
          ticket_id: selectedTicketId,
          booking_id: booking_id,
          event_id: EVENT_ID,
          email: customerData?.email,
          payment_method: paymentMethod,
          coupon_id: couponData?.valid ? couponData?.coupon_id : '',
        };

        const response = await axios(
          `${process.env.REACT_APP_API_URL}/seat-payment/select-seat`,
          {
            headers: {
              'Content-Type': 'application/json',
              token: process.env.REACT_APP_API_KEY,
            },
            method: 'POST',
            data: JSON.stringify(params),
          },
        );

        if (response.data.status) {
          if (response.data.data.type === 'free') {
            toast.success(
              'Kursi berhasil direservasi. Invoice dikirim ke email Anda.',
            );
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } else if (response.data.data.type === 'paid') {
            window.location.href = response.data.data.redirect;
          } else {
            setDisableRes(false);
            toast.error('Kursi sudah dipesan, mohon maaf');
          }
        } else {
          setDisableRes(false);
          toast.error(
            response.data.message ||
              'Reservasi kursi gagal. Silakan coba lagi.',
          );
        }
      } catch (error) {
        setDisableRes(false);
        toast.error('Gagal menghubungi server. Silakan coba lagi.');
      }
    } else {
      toast.error('Tidak ada kursi yang dipilih');
    }
  };

  const getSeatMap = async (ids) => {
    try {
      setSelectedIds(ids.map((item) => item.id));
      // Endpoint ini mengembalikan seluruh denah acara; ticket_id dan loc2stage
      // yang dulu ikut dikirim diabaikan server (sudah diuji: hasilnya sama
      // persis). Penyaringan per kategori dilakukan di komponen denah.
      const params = { event_id: EVENT_ID };
      const response = await axios(
        `${process.env.REACT_APP_API_URL}/seat-payment/get-dataMaps`,
        {
          headers: {
            'Content-Type': 'application/json',
            token: process.env.REACT_APP_API_KEY,
          },
          method: 'POST',
          data: JSON.stringify(params),
        },
      );

      if (response.data.status) {
        setSeatMap(Array.isArray(response.data.data) ? response.data.data : []);
        setModalSeatMap(true);
      } else {
        toast.error('Denah kursi tidak tersedia saat ini.');
      }
    } catch (error) {
      toast.error('Gagal memuat denah kursi. Silakan coba lagi.');
    }
  };

  function getAllId(options) {
    const ids = [];
    options.forEach((group) => {
      ids.push(group.id);
    });

    setSeatMapSelectId(ids);
  }

  function getAllNamesNoLoc(options) {
    const names = [];
    options.forEach((group) => {
      names.push(group.name);
    });

    return names.join(', ');
  }

  function getTotalPrice(options) {
    let totalSeatCharged = 0;

    options.forEach((group) => {
      if (group.seat_charged !== null) {
        totalSeatCharged += parseFloat(group.seat_charged);
      }
    });

    return totalSeatCharged;
  }

  const handleSelectSeat = (index, option) => {
    setIndexSelect(index);
    setListSeatSelected(getAllNamesNoLoc(option));
    setPriceSeatSelected(getTotalPrice(option));
    setSelectedTicketId(option[0].ticket_id);
    getAllId(option);
    scrollToSection(section3Ref);
  };

  const combineTickets = (data) => {
    const result = {};

    data.forEach((item) => {
      const { ticket_name, seats } = item;

      if (!result[ticket_name]) {
        result[ticket_name] = {
          ticket_name,
          seats: [],
        };
      }

      result[ticket_name].seats.push(...seats);
    });

    // Ubah objek menjadi array dan gabungkan seats dengan koma
    return Object.values(result).map((item) => ({
      ticket_name: item.ticket_name,
      seats: item.seats.join(', '),
    }));
  };

  // Sebuah susunan hanya boleh dipakai kalau jumlah kursinya persis sama
  // dengan yang dibayar. Kalau allocated_seat belum diketahui (0), jangan
  // memblokir apa pun.
  const isUsableOption = (option) =>
    requiredSeatCount === 0 || option.length === requiredSeatCount;

  // Indeks asli ikut dibawa supaya penyaringan dan pengurutan tidak merusak
  // penanda "sedang dipilih", yang mengacu ke posisi di optionSeat.
  const decoratedOptions = optionSeat.map((option, index) => ({
    index,
    option,
    price: getTotalPrice(option),
    block: blockLabel(option[0].loc2stage),
    usable: isUsableOption(option),
  }));

  const usablePrices = decoratedOptions
    .filter((entry) => entry.usable)
    .map((entry) => entry.price);
  const hasUsableOption = usablePrices.length > 0;
  const cheapestPrice = hasUsableOption ? Math.min(...usablePrices) : null;
  // Lencana "Termurah" hanya berguna kalau harganya memang bervariasi.
  const showCheapestBadge =
    hasUsableOption && Math.max(...usablePrices) > cheapestPrice;

  const availableBlocks = [
    ...new Set(decoratedOptions.map((entry) => entry.block).filter(Boolean)),
  ].sort();

  const visibleOptions = decoratedOptions
    .filter((entry) => blockFilter === 'all' || entry.block === blockFilter)
    .filter((entry) => !onlyUsable || entry.usable)
    .sort((a, b) => {
      if (sortBy === 'price-asc') return a.price - b.price || a.index - b.index;
      if (sortBy === 'price-desc')
        return b.price - a.price || a.index - b.index;
      return a.index - b.index;
    });

  const mismatchCount = decoratedOptions.filter(
    (entry) => !entry.usable,
  ).length;

  const canReserve =
    seatMapSelectId.length > 0 &&
    (requiredSeatCount === 0 || seatMapSelectId.length === requiredSeatCount);

  const discountValue = couponData?.valid
    ? couponData?.type === 'percentage'
      ? ((priceSeatSelected || 0) * (couponData?.value || 0)) / 100
      : couponData?.value || 0
    : 0;

  return (
    <Container className="home text-white" fluid>
      <Modal
        size="xl"
        centered
        scrollable
        className="modal-seatmap"
        show={modalSeatMap}
        onHide={() => setModalSeatMap(false)}
      >
        <Modal.Header closeButton>
          <b>Seat Map ({selectedSeatIds?.length} Seat)</b>
        </Modal.Header>
        <Modal.Body style={{ overflowX: 'auto', overflowY: 'auto' }}>
          {selectedTicketIdRes === '93' ? (
            <SeatMapCircle
              seatData={seatMap?.filter(
                (i) => i.ticket_id === 93 && i.id >= 4523,
              )}
              filter={selectedTicketIdRes}
              selectSeatIds={selectedSeatIds}
            />
          ) : (
            <Seat
              seatData={seatMap}
              filter={selectedTicketIdRes}
              selectSeatIds={selectedSeatIds}
            />
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="btn-select-seat-select"
            onClick={() => setModalSeatMap(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Ringkasan Pesanan */}
      <Modal
        show={modalInvoice}
        onHide={() => setModalInvoice(false)}
        className="modal-order-summary modal-bottom"
        animation={true}
      >
        <Modal.Body>
          <div className="summary-grabber" aria-hidden="true" />
          <div className="summary-header">
            <h5 className="m-0">Ringkasan Pesanan</h5>
            <button
              type="button"
              className="summary-close"
              aria-label="Tutup ringkasan pesanan"
              onClick={() => setModalInvoice(false)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 9L12 15L18 9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="summary-body">
            <div className="summary-block">
              <p className="summary-label">Kursi Dipilih</p>
              <p className="summary-seats">{listSeatSelected || '-'}</p>
            </div>

            <div className="summary-row">
              <span className="summary-label">Total Reservasi</span>
              <span className="summary-value">
                Rp {formatNumberWithout(priceSeatSelected || 0)}
              </span>
            </div>

            {couponData?.valid && (
              <div className="summary-row">
                <span className="summary-label">Diskon</span>
                <span className="summary-value text-danger">
                  - Rp {formatNumberWithout(discountValue)}
                </span>
              </div>
            )}

            <div className="summary-divider" />

            <div className="summary-row summary-row-total">
              <span>Total</span>
              <span>
                Rp{' '}
                {formatNumberWithout((priceSeatSelected || 0) - discountValue)}
              </span>
            </div>

            <Button
              type="button"
              className="btn-continue w-100"
              onClick={() => {
                setModalInvoice(false);
                handleReservSeat();
              }}
              disabled={disableRes}
            >
              Lanjut
              <svg
                className="ms-2"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M5 12H19"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 5L19 12L12 19"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <a
        className="btn-goto-wa"
        href={`https://wa.me/${EVENT.csNumber}?text=${encodeURIComponent(
          'Halo Admin Soul, saya ingin bertanya tentang Soul 8.',
        )}`}
        target="_blank"
        rel="noreferrer"
        aria-label="Hubungi admin Soul via WhatsApp"
      >
        <span className="btn-goto-wa-circle">
          <WhatsAppGlyph width={22} height={22} />
        </span>
        <span className="btn-goto-wa-label">Support</span>
      </a>

      <div className="home-shell">
        <header className="home-hero">
          <Image
            className="hero-logo"
            src={ART.logo}
            alt="The Premiere Soul 8 — Intimate Concert"
          />
          <Image
            className="hero-art"
            src={ART.hero}
            alt="Ebiet G. Ade dan Iwan Fals & Band"
          />
          <div className="event-meta">
            <ul>
              <li>
                <MapPin size={18} aria-hidden="true" />
                <span>{EVENT.venue}</span>
              </li>
              <li>
                <Calendar size={18} aria-hidden="true" />
                <span>{EVENT.date}</span>
              </li>
            </ul>
          </div>
        </header>

        <section className="home-section">
          <SectionTitle>Seating Layout</SectionTitle>
          <div className="panel panel-flush">
            <Image
              className="layout-map"
              src={ART.seatingLayout}
              alt="Denah kategori kursi Soul 8"
              loading="lazy"
            />
          </div>
        </section>

        <section className="home-section">
          <Form
            className="ticket-box"
            onSubmit={(e) => {
              e.preventDefault();
              emailStatus ? handleRemoveState() : handleBooking();
            }}
          >
            <Form.Label htmlFor="email">Email</Form.Label>
            <InputGroup className="input-group-soul">
              <Form.Control
                type="email"
                id="email"
                className="input-email"
                onChange={(e) => setEmailVal(e.target.value)}
                value={emailVal}
                placeholder="Masukkan Email"
                required
                readOnly={emailStatus}
              />
              <Button type="submit" className="input-email-check">
                {emailStatus ? 'Ganti' : 'Check'}
              </Button>
            </InputGroup>
            <p className="field-hint">
              <Info className="field-hint-icon" size={20} aria-hidden="true" />
              <span>
                Masukkan email yang Anda gunakan saat memesan tiket untuk
                memilih kursi.
              </span>
            </p>
          </Form>
        </section>

        {bookingStatus && (
          <>
            <section className="home-section">
              <div className="panel">
                <SectionTitle>Booking Info</SectionTitle>
                <div className="booking-detail">
                  <p className="booking-detail-row">
                    <Mail size={18} aria-hidden="true" />
                    <span>{customerData?.email}</span>
                  </p>
                  <p className="booking-detail-row">
                    <User size={18} aria-hidden="true" />
                    <span>{customerData?.name}</span>
                  </p>
                </div>
              </div>
            </section>

            {seatBooked.length > 0 && (
              <section className="home-section">
                <SectionTitle>Kursi Anda</SectionTitle>
                <div className="panel">
                  <div className="booked-seat-table">
                    <Row className="booked-seat-head">
                      <Col xs={4} className="seat_pick_ul_line">
                        Kategori
                      </Col>
                      <Col xs={8} className="seat_pick_ul">
                        Kursi
                      </Col>
                    </Row>
                    {seatBooked?.map((item, i) => (
                      <Row key={i}>
                        <Col xs={4} className="seat_pick_ul_s_line">
                          {item?.ticket_name}
                        </Col>
                        <Col xs={8} className="seat_pick_ul_s">
                          {item?.seats}
                        </Col>
                      </Row>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {bookingData.length > 0 && (
              <>
                <section className="home-section">
                  <SectionTitle>Pilih Kursi</SectionTitle>
                  <Tabs
                    className="tabs-ticket"
                    activeKey={key}
                    onSelect={(k) => {
                      const selectedBooking = bookingData.find(
                        (booking) =>
                          `${booking.ticket_id}-${booking.booking_code}` === k,
                      );

                      setKey(k);
                      getRecomendation(k, selectedBooking?.allocated_seat);
                    }}
                    mountOnEnter
                  >
                    {bookingData?.map((item, i) => (
                      <Tab
                        key={i}
                        eventKey={`${item.ticket_id}-${item.booking_code}`}
                        title={
                          <>
                            <span className="tab-name">{item.name}</span>
                            <span className="tab-meta">
                              {item.allocated_seat} Seat
                            </span>
                          </>
                        }
                      >
                        <div className="reco-wrap">
                          {recoStatus.state === 'loading' ? (
                            <p className="reco-empty">
                              Memuat rekomendasi kursi…
                            </p>
                          ) : recoStatus.state === 'error' ? (
                            <div className="reco-blocked" role="alert">
                              <AlertTriangle size={16} aria-hidden="true" />
                              <div>
                                <p className="m-0">
                                  Rekomendasi kursi gagal dimuat.{' '}
                                  {recoStatus.message}
                                </p>
                                <button
                                  type="button"
                                  className="reco-retry"
                                  onClick={reloadRecommendations}
                                >
                                  Coba lagi
                                </button>
                              </div>
                            </div>
                          ) : optionSeat?.length ? (
                            <>
                              <p className="reco-intro">
                                {requiredSeatCount > 0 ? (
                                  <>
                                    Booking Anda{' '}
                                    <b>{requiredSeatCount} kursi</b>.{' '}
                                  </>
                                ) : null}
                                Pilih salah satu susunan di bawah — harga sudah
                                termasuk seluruh kursi dalam susunan itu.
                              </p>

                              {!hasUsableOption && (
                                <p className="reco-blocked" role="alert">
                                  <AlertTriangle size={16} aria-hidden="true" />
                                  <span>
                                    Tidak ada susunan yang jumlahnya pas{' '}
                                    {requiredSeatCount} kursi. Reservasi
                                    dinonaktifkan — silakan hubungi admin lewat
                                    tombol Support.
                                  </span>
                                </p>
                              )}

                              <div className="reco-controls">
                                <label htmlFor="reco-sort">
                                  <span>Urutkan</span>
                                  <select
                                    id="reco-sort"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                  >
                                    <option value="default">
                                      Rekomendasi sistem
                                    </option>
                                    <option value="price-asc">
                                      Harga terendah
                                    </option>
                                    <option value="price-desc">
                                      Harga tertinggi
                                    </option>
                                  </select>
                                </label>

                                {availableBlocks.length > 1 && (
                                  <label htmlFor="reco-block">
                                    <span>Blok</span>
                                    <select
                                      id="reco-block"
                                      value={blockFilter}
                                      onChange={(e) =>
                                        setBlockFilter(e.target.value)
                                      }
                                    >
                                      <option value="all">Semua</option>
                                      {availableBlocks.map((block) => (
                                        <option key={block} value={block}>
                                          Blok {block}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                )}

                                {mismatchCount > 0 && (
                                  <label
                                    className="reco-toggle"
                                    htmlFor="reco-usable"
                                  >
                                    <input
                                      id="reco-usable"
                                      type="checkbox"
                                      checked={onlyUsable}
                                      onChange={(e) =>
                                        setOnlyUsable(e.target.checked)
                                      }
                                    />
                                    <span>
                                      Sembunyikan {mismatchCount} susunan yang
                                      tidak sesuai
                                    </span>
                                  </label>
                                )}
                              </div>

                              {visibleOptions.length === 0 ? (
                                <p className="reco-empty">
                                  Tidak ada susunan yang cocok dengan filter
                                  ini.
                                </p>
                              ) : (
                                <ul className="reco-list">
                                  {visibleOptions.map(
                                    ({
                                      index: i,
                                      option,
                                      price,
                                      block,
                                      usable,
                                    }) => (
                                      <li key={i}>
                                        <article
                                          className={`reco-card${
                                            indexSelect === i
                                              ? ' is-selected'
                                              : ''
                                          }${usable ? '' : ' is-invalid'}`}
                                        >
                                          <div className="reco-head">
                                            {block && (
                                              <span className="reco-block">
                                                Blok {block}
                                              </span>
                                            )}
                                            <span className="reco-count">
                                              {option.length} kursi
                                            </span>
                                            {usable &&
                                              showCheapestBadge &&
                                              price === cheapestPrice && (
                                                <span className="reco-badge">
                                                  Termurah
                                                </span>
                                              )}
                                          </div>

                                          <p className="reco-seats">
                                            {summariseSeats(option).join(' • ')}
                                          </p>

                                          {!usable && (
                                            <p className="reco-warning">
                                              <AlertTriangle
                                                size={15}
                                                aria-hidden="true"
                                              />
                                              <span>
                                                Tidak bisa dipilih: berisi{' '}
                                                {option.length} kursi, sedangkan
                                                booking Anda {requiredSeatCount}{' '}
                                                kursi.
                                              </span>
                                            </p>
                                          )}

                                          <div className="reco-foot">
                                            <span
                                              className={`reco-price${
                                                price === 0 ? ' is-free' : ''
                                              }`}
                                            >
                                              {price === 0
                                                ? 'GRATIS'
                                                : `Rp ${formatNumberWithout(price)}`}
                                            </span>
                                            <div className="reco-actions">
                                              <Button
                                                className="btn-map-view"
                                                onClick={() => {
                                                  getAllId(option);
                                                  getSeatMap(
                                                    option,
                                                    option[0].ticket_id,
                                                    option[0].loc2stage,
                                                  );
                                                }}
                                              >
                                                Denah <Eye size={15} />
                                              </Button>
                                              <Button
                                                className={`btn-select-seat${
                                                  indexSelect === i
                                                    ? '-select'
                                                    : ''
                                                }`}
                                                disabled={!usable}
                                                onClick={() =>
                                                  handleSelectSeat(i, option)
                                                }
                                              >
                                                {indexSelect === i
                                                  ? 'Dipilih'
                                                  : 'Pilih'}
                                              </Button>
                                            </div>
                                          </div>
                                        </article>
                                      </li>
                                    ),
                                  )}
                                </ul>
                              )}
                            </>
                          ) : (
                            <p className="reco-empty">
                              Belum ada rekomendasi kursi untuk kategori ini.
                            </p>
                          )}
                        </div>
                      </Tab>
                    ))}
                  </Tabs>
                </section>

                <section className="home-section" ref={section3Ref}>
                  <ul className="home-notes">
                    <li>
                      Batas reservasi hanya{' '}
                      <b className="text-warning">15 menit</b> sampai waktu
                      pembayaran habis.
                    </li>
                    <li>
                      Jika Anda tidak memilih kursi, sistem akan otomatis
                      menentukan nomor kursi <b>(FREE)</b>.
                    </li>
                    <li>
                      Invoice reservasi kursi akan dikirim ke email Anda setelah
                      dipesan.
                    </li>
                  </ul>

                  {priceSeatSelected !== 0 && priceSeatSelected !== '' && (
                    <>
                      <h3 className="home-subtitle">Metode Pembayaran</h3>
                      <div
                        className="ticket-box-payment"
                        role="radiogroup"
                        aria-label="Metode pembayaran"
                      >
                        {PAYMENT_METHODS.map(({ code, label, logo }) => (
                          <label
                            key={code}
                            htmlFor={`payment-${code}`}
                            className={`payment-select${
                              paymentMethod === code ? ' is-active' : ''
                            }`}
                          >
                            <input
                              className="form-check-input"
                              type="radio"
                              id={`payment-${code}`}
                              name="payment_method"
                              value={code}
                              checked={paymentMethod === code}
                              onChange={(e) => setPaymentMethod(e.target.value)}
                            />
                            <span className="payment-logo">
                              <img src={logo} alt="" aria-hidden="true" />
                            </span>
                            <span className="payment-name">{label}</span>
                          </label>
                        ))}
                      </div>
                    </>
                  )}

                  <div className="summary-fields">
                    <Form.Label htmlFor="selected-seat">
                      Kursi Dipilih
                    </Form.Label>
                    <Form.Control
                      id="selected-seat"
                      className="input-email input-readonly"
                      readOnly
                      placeholder="Belum ada kursi dipilih"
                      value={listSeatSelected}
                    />
                    <Form.Label htmlFor="selected-total">
                      Total Reservasi
                    </Form.Label>
                    <Form.Control
                      id="selected-total"
                      className="input-email input-readonly"
                      readOnly
                      value={`Rp ${formatNumberWithout(priceSeatSelected || 0)}`}
                    />
                    <Form.Label htmlFor="kupon">Kode Kupon</Form.Label>
                    <InputGroup className="input-group-soul">
                      <Form.Control
                        type="text"
                        id="kupon"
                        className="input-email"
                        onChange={(e) => setCoupon(e.target.value)}
                        value={coupon}
                        placeholder="Masukkan kode kupon"
                      />
                      <Button
                        type="button"
                        className="input-email-check"
                        onClick={() => handleValidateCoupon()}
                      >
                        Pakai Kupon
                      </Button>
                    </InputGroup>
                  </div>

                  <Button
                    type="button"
                    className="btn-buy-now-ticket"
                    disabled={disableRes || !canReserve}
                    onClick={() => setModalInvoice(true)}
                  >
                    Reservasi Kursi
                  </Button>
                </section>
              </>
            )}
          </>
        )}

        <footer className="home-footer">
          <p className="footer-copy">
            © {new Date().getFullYear()} Soul Concert. All rights reserved.
          </p>
          <div className="footer-contacts">
            <a
              className="footer-contact"
              href={`https://wa.me/${EVENT.csNumber}`}
              target="_blank"
              rel="noreferrer"
            >
              <span className="footer-badge">
                <WhatsAppGlyph width={16} height={16} />
              </span>
              {EVENT.csLabel}
            </a>
            <a
              className="footer-contact"
              href={`https://instagram.com/${EVENT.instagram}`}
              target="_blank"
              rel="noreferrer"
            >
              <span className="footer-badge">
                <Instagram size={16} aria-hidden="true" />
              </span>
              @{EVENT.instagram}
            </a>
          </div>
        </footer>
      </div>
    </Container>
  );
};

export default Home;
